import { Cancel, Mail, NotificationsOutlined, ReceiptLongOutlined, RuleFolder, Warning } from "@mui/icons-material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PeopleIcon from "@mui/icons-material/People";
import {
  Box,
  Button,
  GlobalStyles,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";
import { ApexOptions } from "apexcharts";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import {
  Building,
  ClipboardCheck,
  ClipboardList,
  CreditCard,
  Factory,
  FileCheck,
  FileText,
  Package,
  Receipt,
  RotateCcw,
  ScrollText,
  ShoppingBag,
  ShoppingCart,
  User,
  UserPlus,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CAN_ACCESS_APPLICATION,
  CAN_ACCESS_KPI_CONSTANTS,
  CAN_ACCESS_QUICK_ACTION,
  CREATE_PATIENT_FLOW_SOURCE,
  USER_ROLES,
} from "../../constants/applicationConstants";
import useToken from "../../hooks/useToken";
import {
  selectIsTokenAvailable,
  selectIsValueFoundInToken,
  selectTokenValues,
  useAuthSlice,
} from "../../redux/slice/authSlice";
import {
  CountRange,
  fetchActivePracticesKPIData,
  fetchActiveUsersKPIData,
  fetchIvrBreakdownData,
  fetchMonthlyIvrCountsTwelveMonths,
  fetchMonthlyOrderCountsTwelveMonths,
  fetchOrderBreakdownData,
  fetchTotalPatientsKPIData,
  fetchWeeklyOrdersKPIData,
  fetchWeeklyRecordTxPercentage,
  fetchWeeklySalesKPIData,
  resetDashboardData,
  useDashboardReducer,
} from "../../redux/slice/dashboard/dashboardSlice";
import {

  fetchDahboardSentboxMessageList,
  fetchDashboardInboxMessageList,
  fetchDashboardNotificationList,
  fetchImportantNotifications,
  notificationAttachmentDTO,
  resetNotificationList,
  useCreateNotificationSlice,
} from "../../redux/slice/inbox/createNotificationSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { RootState } from "../../redux/store";
import { tokens } from "../../theme";
import { sideBarItems } from "../common/sidebar/MyProSidebar";
import AreaChart from "./AreaChart";
// import { fetchActivePracticesKPIDataDateRange, fetchActiveUsersKPIDataDateRange, fetchIvrBreakdownDataDateRange, fetchOrderBreakdownDataDateRange, fetchTotalPatientsKPIDataDateRange, fetchWeeklyOrdersKPIDataDateRange, fetchWeeklyRecordTxPercentageDateRange, fetchWeeklySalesKPIDataDateRange } from "../../redux/slice/dashboard/dashboardSlice";
import KPIDetailModal from "./KPIDetailModal";
import StatusBreakdown from "./StatusBreakdown";
import TimePanel from "./TimePanel";
import TotalActiveUsersCard, { KPIType } from "./TotalActiveUsersCard";
import CompletePracticeRegistrationModal from "../operation/account/CompletePracticeRegistrationModal";
import OrderProductModal from "../operation/account/OrderProductModal";
import { setSingleAlertObj, showHideLoadingDialog, SingleAlertInfo } from "../../redux/slice/commonSlice";
import React from "react";
import { NotificationData } from "../operation/notification/NotificationList";
import { formatAuditTimestamp } from "../../utils/Utility";
import { InboxData } from "../operation/inbox/InboxList";
import { CommunicationNotificationsPanel } from "./CommunicationNotificationsPanel";
import { CommunicationInboxPanel } from "./CommunicationInboxPanel";
import AddPatientModal from "../operation/account/AddPatientModal";
import AddNewPatientPopup from "../operation/patients/AddNewPatientPopup";
import CreateIVRModal from "../operation/account/CreateIVRModal";
import RecordTreatmentModal from "../operation/account/RecordTreatmentModal";
import ReturnProductModal from "../operation/account/ReturnProductModal";
import PayInvoiceModal from "../operation/account/PayInvoiceModal";
import { fetchUserProfileById, useUserReducer } from "../../redux/slice/userSlice";
import WarningPopupNew, { WarningPopupNewObjectProps } from "../common/WarningPopupNew";
import StripePaymentTypePopup from "../operation/payment/stripe/StripePaymentTypePopup";
import { getInvoiceById, invoiceInfo, stripeCheckOutPaymentData } from "../../redux/slice/invoice/InvoiceSlice";
import { fetchDocumentById, fetchPracticeById } from "../../redux/slice/practiceSlice";
import PDFViewer from "../common/pdf/PDFViewer";
import ManagePracticeModal, { practiceFormData } from "../operation/account/ManagePracticeModal";
import ManageProductModal, { productFormData } from "../operation/account/ManageProductModal";
import ManageManufacturerModal, { ManufacturerFormData } from "../operation/account/ManageManufacturerModal";
import { fetchManufacturerDataById } from "../../redux/slice/manufacturer/ManufacturerSlice";
import CreateOrApplyInvoiceModal from "../operation/account/CreateOrApplyInvoiceModal";
import ManageUserModal, { userFormData } from "../operation/account/ManageUserModal";
import RecordCreditMemoModal, { RecordCreditMemoFormData } from "../operation/account/RecordCreditMemoModal";
import { SUCCESS_CODE } from "../../service/ServiceConstants";

const NotificationContainer = ({ headerAdornment }: { headerAdornment?: React.ReactNode }) => {
  const theme = useTheme();
  const [notifTab, setNotifTab] = useState<"unread" | "acknowledged">("unread");
  const { dashboardNotificationListInfo } = useCreateNotificationSlice();
  const dispatch = useAppDispatch();
  const accessibleNotifications = useSelector((state: RootState) => selectTokenValues("canAccessNotifications")(state));
  const [isUnread, setIsUnread] = React.useState<boolean>(true);
  const { userInfo } = useAuthSlice();
  const [notificationData, setNotificationData] = React.useState<NotificationData[]>([]);
  const isDark = theme.palette.mode === "dark";
  const isRestrictedUser = userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU;

  const fetchInitialNotificationList = async () => {
    dispatch(showHideLoadingDialog(true));
    const responseNotifs = await dispatch(fetchDashboardNotificationList({
      notificationTypes: accessibleNotifications ? accessibleNotifications : [],
      unread: isUnread,
      notificationCategory: 'system',
      currentPage: 1,
      itemsPerPage: 5
    }));
    dispatch(showHideLoadingDialog(false));
  }

  useEffect(() => {
    if (notifTab === 'unread') {
      setIsUnread(true);
    } else {
      setIsUnread(false);
    }
  }, [notifTab])

  useEffect(() => {
    fetchInitialNotificationList();
  }, [isUnread]);

  useEffect(() => {
    if (!dashboardNotificationListInfo || (dashboardNotificationListInfo.content[0] && dashboardNotificationListInfo.content[0].senderId != null)) return;

    const mappedData: NotificationData[] = dashboardNotificationListInfo.content.map((item: any) => {
      let date = "";
      let time = "";
      let createdDate = "";
      let createdTime = "";
      let ackUserName = "";
      let formattedDate = "";
      let formattedTime = "";
      if (isUnread) {
        const assignedToEntry = item.notificationAsgnTo?.find(
          (assign: any) => assign.assignedToUserId === userInfo.userId
        );
        if (assignedToEntry) {
          const formatted = formatAuditTimestamp(item.assignedToAuditTimeStamp);
          const [splitDate, splitTime] = assignedToEntry.assignedToAuditTimeStamp.split(" ");
          date = splitDate || "";
          time = splitTime + " " + assignedToEntry.assignedToAuditTimeStamp.split(" ")[2];
          formattedDate = formatted.date;
          formattedTime = formatted.time;
        } else {
          if (item.auditTimeStamp && item.auditTimeStamp != null) {
            const [splitDate, splitTime] = item.auditTimeStamp.split(" ");
            const formatted = formatAuditTimestamp(item.auditTimeStamp);
            date = splitDate || "";
            time = splitTime + " " + item.auditTimeStamp.split(" ")[2];
            formattedDate = formatted.date;
            formattedTime = formatted.time;
          }
        }
      } else {
        if (item.notificationAck.length > 0) {
          const ackEntry = item.notificationAck?.find(
            (ack: any) => ack.ackUsrId === userInfo.userId
          );

          if (ackEntry) {
            const [splitDate, splitTime] = ackEntry.ackAuditTimeStamp.split(" ");
            const formatted = formatAuditTimestamp(ackEntry.ackAuditTimeStamp);
            date = splitDate || "";
            time = splitTime + " " + ackEntry.ackAuditTimeStamp.split(" ")[2];
            formattedDate = formatted.date;
            formattedTime = formatted.time;
            ackUserName = ackEntry.ackUserName;
            if (ackEntry.ackUserEmail === userInfo.userName) {
              ackUserName = ackUserName + " (You)"
            }
          } else {
            const [splitDate, splitTime] = (item.notificationAck[0].ackAuditTimeStamp || "").split(" ");
            const formatted = formatAuditTimestamp(item.notificationAck[0].ackAuditTimeStamp);
            date = splitDate || "";
            time = splitTime + " " + (item.notificationAck[0].ackAuditTimeStamp || "").split(" ")[2];
            formattedDate = formatted.date;
            formattedTime = formatted.time;
            ackUserName = item.notificationAck[0].ackUserName;
            if (item.notificationAck[0].ackUserEmail === userInfo.userName) {
              ackUserName = ackUserName + " (You)"
            }
          }

          const [splitCreatedDate, splitCreatedTime] = item.auditTimeStamp.split(" ");
          const formatted = formatAuditTimestamp(item.auditTimeStamp);
          // createdDate = splitCreatedDate || "";
          // createdTime = splitCreatedTime + " " + item.auditTimeStamp.split(" ")[2];
          createdDate = formatted.date;
          createdTime = formatted.time;
        }
      }

      return {
        notification: item,
        notificationId: item.notificationId,
        entityId: item.entityId,
        entity: item.entity,
        title: item.notificationSub || "",
        description: item.notificationMsg || "",
        date,
        time,
        createdDate,
        createdTime,
        ackedByUserName: ackUserName,
        acknowledged: item.acknowledged || false,
        hasEntity: (item.entity !== null && item.entityId !== null) || isRestrictedUser,
        isAssignable: (item.notificationType === "TO_USER" || (item.notificationType === "TO_ADMIN" && item.targetEntityId != 0)) ? true : false,
        attachments: item.notificationAttachment && item.notificationAttachment.length > 0 ? item.notificationAttachment : [],
        formattedDate,
        formattedTime
      };
    });
    setNotificationData(mappedData);
  }, [dashboardNotificationListInfo]);


  const getLabel = (dateStr: string) => {
    const date = dayjs(dateStr);
    const today = dayjs();
    const yesterday = today.subtract(1, "day");

    if (date.isSame(today, "day")) return "Today";
    if (date.isSame(yesterday, "day")) return "Yesterday";
    if (date.isAfter(today.subtract(7, "day"))) return "Last 7 days";
    if (date.month() === today.month() && date.year() === today.year())
      return "Earlier this month";

    return date.format("MMMM YYYY");
  };

  const grouped = notificationData?.reduce((acc, item) => {
    const label = getLabel(item.date);
    if (!acc[label]) acc[label] = [];
    acc[label].push(item);
    return acc;
  }, {} as Record<string, NotificationData[]>);

  const effectiveGrouped = isRestrictedUser
    ? (() => {
        const result: Record<string, NotificationData[]> = {};
        notificationData.forEach((item, idx) => {
          const label = idx < Math.ceil(notificationData.length / 2) ? "Today" : "Yesterday";
          if (!result[label]) result[label] = [];
          result[label].push(item);
        });
        return result;
      })()
    : grouped;

  const sortPriority = [
    "Today",
    "Yesterday",
    "Last 7 days",
    "Earlier this month",
  ];

  const sortedGroups = Object.keys(effectiveGrouped).sort((a, b) => {
    const aIndex = sortPriority.indexOf(a);
    const bIndex = sortPriority.indexOf(b);

    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    else if (aIndex !== -1) return -1;
    else if (bIndex !== -1) return 1;
    else return dayjs(b).toDate().getTime() - dayjs(a).toDate().getTime();
  });

  const refreshPage = () => {
    fetchInitialNotificationList();
  };

  //unmount
  useEffect(() => () => {
    dispatch(resetNotificationList());
  }, []);

  console.log(dashboardNotificationListInfo)
  return (
    <CommunicationNotificationsPanel
      notifTab={notifTab}
      onTabChange={setNotifTab}
      isUnread={isUnread}
      onRefresh={refreshPage}
      grouped={effectiveGrouped}
      sortedGroups={sortedGroups}
      tabArray={["unread", "acknowledged"]}
      NotificationListInfo={dashboardNotificationListInfo}
      maxRows={4}
      headerAdornment={headerAdornment}
    />
  );
};

const InboxContainer = ({ headerAdornment }: { headerAdornment?: React.ReactNode }) => {
  const dispatch = useAppDispatch();
  const accessibleNotifications = useSelector((state: RootState) => selectTokenValues("canAccessNotifications")(state));
  const [notifTab, setNotifTab] = useState<"unread" | "read" | "sent">("unread");
  const { userInfo } = useAuthSlice();
  const { dashboardInboxListInfo } = useCreateNotificationSlice();
  const [inboxData, setInboxData] = React.useState<InboxData[]>([]);

  const fetchInitialSentBox = async () => {
    dispatch(showHideLoadingDialog(true));
    const responseNotifs = await dispatch(
      fetchDahboardSentboxMessageList({
        notificationTypes: [
          "TO_SUPER_ADMIN",
          "TO_ADMIN",
          "TO_USER",
          "BROADCAST",
          "TO_MANU",
          "TO_PRACTICE",
          "TO_AGENCY",
        ],
        currentPage: 1,
      })
    );
    dispatch(showHideLoadingDialog(false));
  };

  const refreshPage = () => {
    if (notifTab === "unread" || notifTab === "read") {
      fetchInitialNotificationList();
    } else if (notifTab === "sent") {
      fetchInitialSentBox();
    }
  };

  useEffect(() => {
    if (
      !dashboardInboxListInfo ||
      (dashboardInboxListInfo.content[0] && dashboardInboxListInfo.content[0].senderId == null)
    )
      return;

    const mappedData: InboxData[] = dashboardInboxListInfo.content.map((item: any) => {
      let date = "";
      let time = "";
      let formattedDate = "";
      let formattedTime = "";

      const assignedToEntry = item.notificationAsgnTo?.find(
        (assign: any) => assign.assignedToUserId === userInfo.userId
      );
      if (assignedToEntry) {
        const [splitDate, splitTime] =
          assignedToEntry.assignedToAuditTimeStamp.split(" ");
        const formatted = formatAuditTimestamp(assignedToEntry.assignedToAuditTimeStamp);
        date = splitDate || "";
        time =
          splitTime +
          " " +
          assignedToEntry.assignedToAuditTimeStamp.split(" ")[2];
        formattedDate = formatted.date;
        formattedTime = formatted.time;
      } else {
        const [splitDate, splitTime] = item.auditTimeStamp.split(" ");
        const formatted = formatAuditTimestamp(item.auditTimeStamp);
        date = splitDate || "";
        time = splitTime + " " + item.auditTimeStamp.split(" ")[2];
        formattedDate = formatted.date;
        formattedTime = formatted.time;
      }

      const isMulti = (item.parentNotification != null || (item.childNotifications && item.childNotifications.length>0)) ? true : false;
      let displayName = "";
      let displayMsg = "";
      let displayDoc;

      switch (notifTab) {
        case "unread":
          displayName = item.senderName;
          displayMsg = item.notificationMsg;
          displayDoc = item.pfpMap?.[item.senderId] || null;
          break;

        case "read": {
          const parent = item.parentNotification;
          displayName = parent?.senderName ?? item.senderName;
          displayMsg = parent?.notificationMsg ?? item.notificationMsg;
          displayDoc = isMulti ? item.pfpMap?.[item.targetEntityId] : item.pfpMap?.[item.senderId];
          break;
        }

        case "sent":
          displayName = item.targetName;
          displayMsg = item.notificationMsg;
          displayDoc = item.pfpMap?.[item.targetEntityId];
          break;
      };

      return {
        notification: item,
        isMulti: isMulti,
        date,
        time,
        formattedDate,
        formattedTime,
        acknowledged: item.acknowledged || false,
        hasEntity: item.entity !== null && item.entityId !== null,
        isAssignable: checkAssignability(item),
        targetEntity: item.targetEntity,
        targetEntityId: item.targetEntityId,
        displayName: displayName,
        displayMsg: displayMsg,
        displayDoc: displayDoc
      };
    });

    setInboxData(mappedData);
  }, [dashboardInboxListInfo]);



  const fetchInitialNotificationList = async () => {
    dispatch(showHideLoadingDialog(true));
    const responseNotifs = await dispatch(
      fetchDashboardInboxMessageList({
        notificationTypes: accessibleNotifications
          ? accessibleNotifications
          : [],
        unread: notifTab === 'unread' ? true : false,
        notificationCategory: "user",
        currentPage: 1,
      })
    );
    dispatch(showHideLoadingDialog(false));
  };

  useEffect(() => {
    refreshPage();
  }, []);

  useEffect(() => {
    refreshPage();
  }, [notifTab]);

  const checkAssignability = (message: any) => {
    if ((message.notificationType === "TO_USER" ||
      (message.notificationType === "TO_ADMIN" &&
        message.targetEntityId != 0)) &&
      notifTab === 'unread' &&
      message.senderId != userInfo.userId) {
      return true
    } else {
      return false
    }
  };

  //unmount
  useEffect(() => () => {
    dispatch(resetNotificationList());
  }, []);

  return (
    <CommunicationInboxPanel
      notifTab={notifTab}
      onTabChange={setNotifTab}
      inboxData={inboxData}
      tabArray={["unread", "read", "sent"]}
      isDashboardColor = {true} 
      maxRows = {4}
      hideProfileImage = {false}
      headerAdornment={headerAdornment}/>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const { userInfo } = useAuthSlice();
  const isTokenValueAvailable = useToken();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [addPatientOpen, setAddPatientOpen] = useState(false);
  const [createIVROpen, setCreateIVROpen] = useState(false);
  const [recordTreatmentOpen, setRecordTreatmentOpen] = useState(false);
  const [returnProductOpen, setReturnProductOpen] = useState(false);
  const [payInvoiceOpen, setPayInvoiceOpen] = useState(false);
  const [ showStripePaymentTypePopup, setShowStripePaymentTypePopup ] = useState(false);

  //CS-1745 - START
  const [managePracticeModalOpen, setManagePracticeModalOpen] = useState(false);
  const [manageProductModalOpen, setManageProductModalOpen] = useState(false);
  // const [manageManufacturerModalOpen, setManageManufacturerModalOpen] = useState(false);
  const [createOrApplyInvModalOpen, setCreateOrApplyInvModalOpen] = useState(false);
  const [createAgreementModalOpen, setCreateAgreementModalOpen] = useState(false);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [recordCreditMemoOpen, setRecordCreditMemoOpen] = useState(false);
  //CS-1745 - END

  const { practiceByUserRoleUserSlice } = useUserReducer();

  const [showPRUWarning, setShowPRUWarning] = useState<WarningPopupNewObjectProps>({ show: false, message: '' });
  const isRestrictedUser = userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU;
  const location = useLocation();
  const [registrationModalOpen, setRegistrationModalOpen] = useState(false);
  const [invoicePopupselectedInvoice, setInvoicePopupselectedInvoice] = useState<invoiceInfo | null>(null);
  const [pdfContent, setPDFContent] = useState<string>("");
  const [showPDF, setShowPDF] = useState<{
    isShow: boolean;
    noteName: string;
    fileName: string;
  }>({ isShow: false, noteName: "", fileName: "" });

  const withPRUGuard = (fn: () => void) => () => {
    if (isRestrictedUser) {
      setShowPRUWarning({
        show: true,
        title: "Pre Registered User Warning",
        message: (
          <center>
            {isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
              ?
              "You don't have access to this feature. Please complete the practice registration in order to use the full system functionality."
              : "Your practice submission is pending approval. Full access to the system will be granted once it has been reviewed and approved."}
          </center>
        ),
        variant: "warning",
        buttons: [
          ...(isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
            ? [
              {
                name: "Cancel",
                color: "error" as const,
                icon: <Cancel sx={{ fontSize: "1rem" }} />,
                onClick: () => setShowPRUWarning({ show: false, message: '' }),
              },
            ]
            : [
              {
                name: "OK",
                color: "success" as const,
                onClick: () => setShowPRUWarning({ show: false, message: '' }),
              },
            ]),
          ...(isRestrictedUser && userInfo.associatedPracticeStatus === "PENDING"
          ? [
              {
                name: "Complete Registration",
                color: "success" as const,
                 onClick: () => {
                  setRegistrationModalOpen(true);
                  setShowPRUWarning({ show: false, message: "" });
                },
              },
            ]
          : []),
        ],
      });
      return;
    }
    fn();
  };

  const {
    userStats,
    practiceStats,
    patientStats,
    salesStats,
    weeklyOrdersStats,
    orderBreakdownStats,
    ivrBreakdownStats,
    monthlyOrderCountTwelveMonths,
    monthlyIvrCountTwelveMonths,
    weeklyRecordTxPercentageStats,
    dashboardData,
  } = useDashboardReducer();
  const { ImportantNotificationInfo } = useCreateNotificationSlice();
  const notifications = useSelector(
    (state: RootState) => state.createNotificationSlice.notifications,
  );
  const accessibleNotifications = useSelector((state: RootState) =>
    selectTokenValues("canAccessNotifications")(state),
  );

  const canAccessActiveUserKPI = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.ACTIVE_USERS_KPI)(state),
  );
  const canAccessActivePracticeKPI = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.ACTIVE_PRACTICES_KPI)(state),
  );
  const canAccessWeeklySalesKPI = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.WEEKLY_SALES_KPI)(state),
  );
  const canAccessIVRDonut = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.IVR_DONUT)(state),
  );
  const canAccessOrderDonut = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.ORDER_DONUT)(state),
  );
  const canAccessPatientsKPI = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.PATIENTS_KPI)(state),
  );
  const canAccessWeeklyOrdersKPI = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.WEEKLY_ORDERS_KPI)(state),
  );
  const canAccessRecordTxKPI = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessKPI", CAN_ACCESS_KPI_CONSTANTS.RECORD_TX_KPI)(state),
  );
  const canAccessConfig = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.CONFIG_MANAGEMENT)(state),
  );
  const canAccessInbox = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.INBOX_MANAGEMENT)(state),
  );
  const canAccessNotifications = useSelector((state: RootState) =>
    selectIsValueFoundInToken("canAccessApplication", CAN_ACCESS_APPLICATION.NOTIFICATION_MANAGEMENT)(state),
  );

  const canAddPatientQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.ADD_PATIENT)(state),
  );
  const canSubmitIvrQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.SUBMIT_IVR)(state),
  );
  const canPlaceOrderQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.PLACE_ORDER)(state),
  );
  const canRecordTxQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.RECORD_TX)(state),
  );
  const canReturnProductQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.RETURN_PRODUCT)(state),
  );
  const canPayInvoiceQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.PAY_INVOICE)(state),
  );

  const canAccessIvrListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.IVR_LIST)(state),
  );
  const canAccessOrderListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.ORDER_LIST)(state),
  );
  const canAccessPracticeListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.PRACTICE_LIST)(state),
  );
  const canAccessAgreementListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.PRODUCT_AGREEMENT_LIST)(state),
  );
  const canAccessUserListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.USER_LIST)(state),
  );
  const canAccessInvoiceListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.INVOICE_LIST)(state),
  );
  const canAccessProductListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.PRODUCT_LIST)(state),
  );
  const canAccessPatientListQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.PATIENT_LIST)(state),
  );

  // CS-1745 - START
  const canAccessManageProductQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.MANAGE_PRODUCT)(state),
  );

  const canAccessCreateOrApplyInvQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.CREATE_OR_APPLY_INV)(state),
  );

  const canAccessCreateAgreementQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.CREATE_AGREEMENT)(state),
  );

  const canAccessRecordCreditMemoQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.RECORD_CREDIT_MEMO)(state),
  );

  const canAccessManageUserQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.MANAGE_USER)(state),
  );

  const canAccessManagePracticeQuickAction = useSelector((state: RootState) =>
    selectIsValueFoundInToken("qucikActionFunction", CAN_ACCESS_QUICK_ACTION.MANAGE_PRACTICE)(state),
  );
  // CS-1758 - Facesheet extraction access drives dashboard Add Patient quick action flow
  const canCreatePatientUsingFaceSheet = useSelector((state: RootState) =>
    selectIsTokenAvailable("canCreatePatientUsingFaceSheet")(state),
  );
  // CS-1745 - END

  const [activeTab, setActiveTab] = useState<"dashboard" | "analytics">("dashboard");

  const [importantMsg, setImportantMsg] = useState<{
    messages: {
      notificationId: number;
      subject: string;
      message: string;
      time: string;
      attachments: notificationAttachmentDTO[];
      notificationType: string;
      targetEntityId: number;
      profilePicture?: string;
    }[];
  }>({ messages: [] });

  const cards = sideBarItems.filter(
    (item) => isTokenValueAvailable(item.token) && item.title !== "Home",
  );
  const canAccessBrowseMarket = isTokenValueAvailable(CAN_ACCESS_APPLICATION.PRODUCTS);

  const defaultCards = [
    {
      title: "Account Profile",
      route: "/accountProfile",
      icon: <PeopleIcon />,
      description: "View and manage your personal account settings.",
    },
    ...(canAccessInbox && userInfo?.notificationEnable
      ? [{ title: "Inbox", route: "/inbox_list", icon: <Mail />, description: "Check your latest messages." }]
      : []),
    ...(canAccessNotifications && userInfo?.notificationEnable
      ? [{ title: "Notifications", route: "/notification_list", icon: <NotificationsOutlined />, description: "View all system alerts and user-specific notifications." }]
      : []),
  ];

  const stripOuterParagraphTags = (html: string): string => {
    let result = html.trim();
    if (result.startsWith("<p>")) result = result.replace(/^<p>/i, "");
    if (result.endsWith("</p>")) result = result.replace(/<\/p>$/i, "");
    return result.trim();
  };

  useEffect(() => {
    if (!notifications || notifications[0].senderId == null) return;
    if (userInfo.notificationEnable && notifications.length > 0) {
      if (
        !notifications[0].notificationAckId &&
        notifications[0].important == 1 &&
        accessibleNotifications.includes(notifications[0].notificationType ?? "")
      ) {
        const selectedMsg = notifications[0];
        const msg = {
          notificationId: selectedMsg.notificationId,
          subject: selectedMsg.notificationSub || "<No Subject>",
          message: stripOuterParagraphTags(selectedMsg.notificationMsg || ""),
          time: selectedMsg.auditTimeStamp?.toString() || "",
          attachments: selectedMsg?.notificationAttachment?.length ? selectedMsg.notificationAttachment : [],
          notificationType: selectedMsg.notificationType || "",
          targetEntityId: selectedMsg.targetEntityId || 0,
          profilePicture: selectedMsg.pfpMap && selectedMsg.senderId ? selectedMsg.pfpMap[selectedMsg.senderId] : "",
        };
        if (selectedMsg.senderId && canAccessInbox)
          setImportantMsg((prev) => ({ messages: [msg, ...prev.messages] }));
        if (!selectedMsg.senderId && canAccessNotifications)
          setImportantMsg((prev) => ({ messages: [msg, ...prev.messages] }));
      } else {
        const isAckOnceByOtherUser =
          notifications[0].ackUsrId !== userInfo.userId && notifications[0].ackType === "ACK_ONCE";
        if (typeof notifications[0].ackUsrId !== "undefined" && isAckOnceByOtherUser) {
          setImportantMsg((prev) => ({
            messages: prev.messages.filter(
              (msg) => msg.notificationId !== notifications[0].notificationId,
            ),
          }));
        }
      }
    }
  }, [notifications]);

  useEffect(() => {
    if (userInfo.notificationEnable && ImportantNotificationInfo && ImportantNotificationInfo?.length > 0) {
      const messages = ImportantNotificationInfo.reduce((acc: any[], info: any) => {
        const msg = {
          subject: info.notificationSub || "<No Subject>",
          message: stripOuterParagraphTags(info.notificationMsg) || "",
          notificationId: info.notificationId,
          time: info.auditTimeStamp,
          attachments: info.notificationAttachment.length > 0 ? info.notificationAttachment : [],
          notificationType: info.notificationType,
          targetEntityId: info.targetEntityId,
          senderId: info.senderId ? info.senderId : null,
          profilePicture: info.senderId && info.pfpMap ? info.pfpMap[info.senderId] : "",
        };
        if (msg.senderId && canAccessInbox) acc.push(msg);
        if (!msg.senderId && canAccessNotifications) acc.push(msg);
        return acc;
      }, []);
      setImportantMsg({ messages });
    }
  }, [ImportantNotificationInfo]);

  useEffect(() => {
    return () => { dispatch(resetDashboardData()); };
  }, [dispatch]);

  const notificationPanelItems = useMemo(
    () => (ImportantNotificationInfo ?? []).filter((n: any) => !n.senderId),
    [ImportantNotificationInfo],
  );
  const inboxPanelItems = useMemo(
    () => (ImportantNotificationInfo ?? []).filter((n: any) => !!n.senderId),
    [ImportantNotificationInfo],
  );

  const categories = Array.from({ length: 12 }, (_, i) =>
    dayjs().subtract(11 - i, "month").format("MMM (YYYY)"),
  );

  const areaChartOptions: ApexOptions = {
    chart: { type: "area", height: 350, toolbar: { show: false }, zoom: { enabled: false }, foreColor: "#cbd5e1" },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    fill: { type: "gradient", gradient: { shade: "dark", type: "vertical", opacityFrom: 0.6, opacityTo: 0.1 } },
    grid: { borderColor: "#334155", strokeDashArray: 4 },
    xaxis: { type: "category", categories, labels: { style: { colors: "#94a3b8" } } },
    yaxis: { labels: { style: { colors: "#94a3b8" } } },
    legend: { show: true, position: "top", horizontalAlign: "right", labels: { colors: ["#1bb0a8ff", "#facc15"] }, showForSingleSeries: true },
    colors: ["#1bb0a8ff", "#facc15"],
    tooltip: { theme: "dark" },
    responsive: [
      { breakpoint: 768, options: { chart: { height: 300 }, legend: { show: true, showForSingleSeries: true, position: "bottom", horizontalAlign: "center" }, xaxis: { labels: { rotate: -45 } } } },
      { breakpoint: 480, options: { chart: { height: 250 }, legend: { show: true, showForSingleSeries: true, fontSize: "12px" }, xaxis: { labels: { show: true, rotate: -45 } } } },
    ],
  };

  const areaChartSeries: { name: string; data: number[] }[] = [
    ...(canAccessIVRDonut ? [{ name: "IVR", data: monthlyIvrCountTwelveMonths?.monthlyIvrCounts ?? [] }] : []),
    ...(canAccessOrderDonut ? [{ name: "Order", data: monthlyOrderCountTwelveMonths?.monthlyOrderCounts ?? [] }] : []),
  ];

  useEffect(() => {
    dispatch(fetchActiveUsersKPIData({ skipLoader: true }));
    dispatch(fetchActivePracticesKPIData({ skipLoader: true }));
    dispatch(fetchTotalPatientsKPIData({ skipLoader: true }));
    dispatch(fetchWeeklySalesKPIData({ skipLoader: true }));
    dispatch(fetchWeeklyOrdersKPIData({ skipLoader: true }));
    dispatch(fetchOrderBreakdownData({ skipLoader: true }));
    dispatch(fetchIvrBreakdownData({ skipLoader: true }));
    dispatch(fetchMonthlyOrderCountsTwelveMonths({ skipLoader: true }));
    dispatch(fetchMonthlyIvrCountsTwelveMonths({ skipLoader: true }));
    dispatch(fetchWeeklyRecordTxPercentage({ skipLoader: true }));
    dispatch(fetchImportantNotifications());

    // dispatch(fetchActiveUsersKPIDataDateRange({ fromDate: '', toDate: '', skipLoader : true }));
    // dispatch(fetchActivePracticesKPIDataDateRange({ fromDate: '', toDate: '', skipLoader : true }));
    // dispatch(fetchTotalPatientsKPIDataDateRange({ fromDate: '', toDate: '', skipLoader: true }));
    // dispatch(fetchWeeklySalesKPIDataDateRange({ fromDate: '', toDate: '', skipLoader: true }));
    // dispatch(fetchWeeklyOrdersKPIDataDateRange({ fromDate: '', toDate: '', skipLoader: true }));
    // dispatch(fetchOrderBreakdownDataDateRange({ fromDate: '', toDate: '', skipLoader: true }));
    // dispatch(fetchIvrBreakdownDataDateRange({ fromDate: '', toDate: '', skipLoader: true }));
    // dispatch(fetchWeeklyRecordTxPercentageDateRange({ fromDate: '', toDate: '', skipLoader: true }));

  }, []);

  const useCountArray = (list?: CountRange[]): { counts: number[]; ranges: string[] } => {
    return useMemo(() => {
      if (!list) return { counts: [], ranges: [] };
      return { counts: list.map((item) => item.count), ranges: list.map((item) => `${item.fromDate} - ${item.toDate}`) };
    }, [list]);
  };

  const activeUsersKPIBarData = useCountArray(userStats?.fourWeeksRangeUserCounts);
  const activePracticesKPIBarData = useCountArray(practiceStats?.fourWeekRangePracticeCounts);
  const weeklyOrdersKPIBarData = useCountArray(weeklyOrdersStats?.weeklyOrderCounts);
  const totalPatientsKPIBarData = useCountArray(patientStats?.fourWeeksRangePatientCounts);
  const weeklySalesKPIBarData = useCountArray(salesStats?.weeklySalesCounts);
  const weeklyRecordTxKPIBarData = useCountArray(weeklyRecordTxPercentageStats?.weeklyRecordTxCounts);

  const hasNumbers = (arr?: number[]) => Array.isArray(arr) && arr.some((n) => typeof n === "number");
  const hasAny = (v: unknown) => v !== null && v !== undefined;

  const userKPIReady = hasAny(userStats?.activeUsers) && hasNumbers(userStats?.fourWeeksRangeUserCounts?.map((x) => x.count));
  const practiceKPIReady = hasAny(practiceStats?.activePractices) && hasNumbers(practiceStats?.fourWeekRangePracticeCounts?.map((x) => x.count));
  const patientKPIReady = hasAny(patientStats?.totalPatients) && hasNumbers(patientStats?.fourWeeksRangePatientCounts?.map((x) => x.count));
  const salesKPIReady = hasAny(salesStats?.weeklySales) && hasNumbers(salesStats?.weeklySalesCounts?.map((x) => x.count));
  const ordersKPIReady = hasAny(weeklyOrdersStats?.weeklyOrders) && hasNumbers(weeklyOrdersStats?.weeklyOrderCounts?.map((x) => x.count));
  const weeklyRecordTxKPIReady = hasAny(weeklyRecordTxPercentageStats?.weeklyRecordTx) && hasNumbers(weeklyRecordTxPercentageStats?.weeklyRecordTxCounts?.map((x) => x.count));

  const statusLoading = {
    ivr: canAccessIVRDonut && !ivrBreakdownStats,
    order: canAccessOrderDonut && !orderBreakdownStats,
  };

  const isIVRSeriesReady = Array.isArray(monthlyIvrCountTwelveMonths?.monthlyIvrCounts) && (monthlyIvrCountTwelveMonths!.monthlyIvrCounts || []).length > 0;
  const isOrderSeriesReady = Array.isArray(monthlyOrderCountTwelveMonths?.monthlyOrderCounts) && (monthlyOrderCountTwelveMonths!.monthlyOrderCounts || []).length > 0;
  const areaLoading = (canAccessIVRDonut && !isIVRSeriesReady) || (canAccessOrderDonut && !isOrderSeriesReady);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedKPIType, setSelectedKPIType] = useState<KPIType>("activeUsers");
  // CS-1758 - Dashboard Add Patient quick action now supports Manual Entry / Upload FaceSheet
  const [showAddNewPatientPopup, setShowAddNewPatientPopup] = useState(false);
  const handleOpenModal = useCallback((kpiType: KPIType) => { setSelectedKPIType(kpiType); setModalOpen(true); }, []);
  const handleCloseModal = useCallback(() => { setModalOpen(false); }, []);

  const handleOpenKPIModal = useCallback((kpiType: KPIType) => {
    withPRUGuard(() => handleOpenModal(kpiType))();
  }, [isRestrictedUser, handleOpenModal]);

  // CS-1758 - Preserve existing dashboard manual/EMR patient creation behavior
  const handleManualEntry = () => {
    if (practiceByUserRoleUserSlice && practiceByUserRoleUserSlice.length === 1 && practiceByUserRoleUserSlice[0]?.isEMREnabled) {
      setAddPatientOpen(true);
    } else if (practiceByUserRoleUserSlice && practiceByUserRoleUserSlice?.length > 1) {
      setAddPatientOpen(true);
    } else {
      // CS-1758 - Manual add patient flow source from dashboard quick action
      navigate("/editPatient", {
        state: { patientDetails: { isPatientEdit: false, patientId: null, createPatientFlowSource: CREATE_PATIENT_FLOW_SOURCE.MANUAL } },
      });
    }
  };

  // CS-1758 - Route dashboard quick action through the shared Add New Patient popup when facesheet access exists
  const handleClickAddPatient = () => {
    if (canCreatePatientUsingFaceSheet) {
      setShowAddNewPatientPopup(true);
    } else {
      handleManualEntry();
    }
  };

  /* ── Quick actions ── */
  const quickActions: { label: string; icon: JSX.Element; route?: string; show: boolean; onClick?: () => void }[] = [
    { label: "Add Patient",       icon: <UserPlus       size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAddPatientQuickAction, onClick: withPRUGuard(() => handleClickAddPatient()) },
    { label: "Submit IVR",        icon: <ClipboardCheck size={28} color="#14b8a6" strokeWidth={1.8} />, show: canSubmitIvrQuickAction, onClick: withPRUGuard(() => setCreateIVROpen(true)) },
    { label: "Place Order",       icon: <ShoppingCart   size={28} color="#14b8a6" strokeWidth={1.8} />, show: canPlaceOrderQuickAction, onClick: withPRUGuard(() => setOrderModalOpen(true)) },
    { label: "Record TX",         icon: <ClipboardList  size={28} color="#14b8a6" strokeWidth={1.8} />, show: canRecordTxQuickAction, onClick: withPRUGuard(() => setRecordTreatmentOpen(true)) },
    { label: "Return Product",    icon: <RotateCcw      size={28} color="#14b8a6" strokeWidth={1.8} />, show: canReturnProductQuickAction, onClick: withPRUGuard(() => setReturnProductOpen(true)) },
    { label: "Pay an Invoice",    icon: <Receipt        size={28} color="#14b8a6" strokeWidth={1.8} />, show: canPayInvoiceQuickAction, onClick: withPRUGuard(() => setPayInvoiceOpen(true)) },

    { label: "IVR List",                icon: <RuleFolder     sx={{ color: "#14b8a6", fontSize: 28 }} />, show: canAccessIvrListQuickAction, onClick: withPRUGuard(() => navigate("/ivr_list")) },
    { label: "Order List",              icon: <ScrollText     size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessOrderListQuickAction, onClick: withPRUGuard(() => navigate("/order_list")) },
    { label: "Practice List",           icon: <Building       size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessPracticeListQuickAction, onClick: withPRUGuard(() => navigate("/practice_list")) },
    { label: "Invoice List",            icon: <FileCheck      size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessInvoiceListQuickAction, onClick: withPRUGuard(() => navigate("/invoice_list")) },
    { label: "Product Agreement List",  icon: <FileText       size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessAgreementListQuickAction, onClick: withPRUGuard(() => navigate("/practice_product_agreement")) },
    { label: "User List",               icon: <User           size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessUserListQuickAction, onClick: withPRUGuard(() => navigate("/user_list")) },
    { label: "Product List",            icon: <Package        size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessProductListQuickAction, onClick: withPRUGuard(() => navigate("/products/explore_market")) },
    { label: "Patient List",            icon: <Users          size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessPatientListQuickAction, onClick: withPRUGuard(() => navigate("/patients")) },

    //CS-1745 - START
    { label: "Manage Product",          icon: <Package              size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessManageProductQuickAction, onClick: withPRUGuard(() => setManageProductModalOpen(true)) },
    { label: "Create or Apply Invoice", icon: <ReceiptLongOutlined  sx={{ color: "#14b8a6", fontSize: 28 }} />,     show: canAccessCreateOrApplyInvQuickAction, onClick: withPRUGuard(() => setCreateOrApplyInvModalOpen(true)) },
    { label: "Create Agreement",        icon: <FileText             size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessCreateAgreementQuickAction, onClick: withPRUGuard(() => setCreateAgreementModalOpen(true)) },
    { label: "Record Credit Memo",      icon: <CreditCard           size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessRecordCreditMemoQuickAction, onClick: withPRUGuard(() => setRecordCreditMemoOpen(true)) },
    { label: "Manage User",             icon: <User                 size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessManageUserQuickAction, onClick: withPRUGuard(() => setUserModalOpen(true)) },
    { label: "Manage Practice",         icon: <Building             size={28} color="#14b8a6" strokeWidth={1.8} />, show: canAccessManagePracticeQuickAction, onClick: withPRUGuard(() => setManagePracticeModalOpen(true)) },
    // { label: "Manage Manufacturer",  icon: <Factory              size={28} color="#14b8a6" strokeWidth={1.8} />, show: canPlaceOrderQuickAction, onClick: withPRUGuard(() => setManageManufacturerModalOpen(true)) },
    //CS-1745 - END
  ];

  
const dashboardGlobalStyles = (
  <GlobalStyles styles={{
    "@keyframes marqueeScroll": {
      "0%":   { transform: "translateX(100vw)" },
      "100%": { transform: "translateX(-100%)" },
    },
    "@keyframes pulse": {
      "0%, 100%": { opacity: 1 },
      "50%":      { opacity: 0.4 },
    },
    "@keyframes rp1": {
      "0%":   { transform: "scale(0.6)", opacity: 0.9 },
      "80%":  { transform: "scale(2.8)", opacity: 0 },
      "100%": { transform: "scale(2.8)", opacity: 0 },
    },
    "@keyframes rp2": {
      "0%":   { transform: "scale(0.6)", opacity: 0.7 },
      "80%":  { transform: "scale(2.2)", opacity: 0 },
      "100%": { transform: "scale(2.2)", opacity: 0 },
    },
    "@keyframes rp3": {
      "0%":   { transform: "scale(0.6)", opacity: 0.5 },
      "80%":  { transform: "scale(1.6)", opacity: 0 },
      "100%": { transform: "scale(1.6)", opacity: 0 },
    },
    "@keyframes dot-breathe": {
      "0%, 100%": { transform: "scale(1)",    boxShadow: "0 0 6px 2px rgba(20,184,166,0.7)" },
      "50%":      { transform: "scale(1.35)", boxShadow: "0 0 14px 5px rgba(20,184,166,0.4)" },
    },
    "@keyframes border-glow-pulse": {
      "0%, 100%": { borderColor: "rgba(20,184,166,0.2)", boxShadow: "none" },
      "50%":      { borderColor: "rgba(20,184,166,0.6)", boxShadow: "0 0 22px rgba(20,184,166,0.25), inset 0 0 12px rgba(20,184,166,0.04)" },
    },
    "@keyframes float-up-1": {
      "0%":   { transform: "translateY(0px) translateX(0px)", opacity: 0 },
      "20%":  { opacity: 0.7 },
      "100%": { transform: "translateY(-38px) translateX(6px)", opacity: 0 },
    },
    "@keyframes float-up-2": {
      "0%":   { transform: "translateY(0px) translateX(0px)", opacity: 0 },
      "20%":  { opacity: 0.5 },
      "100%": { transform: "translateY(-28px) translateX(-8px)", opacity: 0 },
    },
    "@keyframes float-up-3": {
      "0%":   { transform: "translateY(0px) translateX(0px)", opacity: 0 },
      "20%":  { opacity: 0.6 },
      "100%": { transform: "translateY(-44px) translateX(3px)", opacity: 0 },
    },
  }} />
);

  const onRecordPayment = (invoice: invoiceInfo) => {
    if (!invoice) return;
    navigate("/record_payment", {
      state: {
        invoiceId: invoice.invoiceId,
        invNumber: invoice.mfgInvoiceNumber,
        invAmt: invoice.invoiceAmount,
        invDate: invoice.mfgInvoiceDate,
        practice: invoice.practiceName,
        mfr: invoice.manufacturerName,
        invoiceAmount: invoice.invoiceAmount,
        creditAmount: invoice.creditAmount
      }
    });
  };

  const handleOpenPaymentInstructionDoc = async (row: invoiceInfo) => {
    const actionResult = await dispatch(fetchDocumentById(row.paymentInstructionValue ?? ""));
    const documentData = actionResult.payload;

    if (documentData && documentData.content) {
      setPDFContent(documentData.content);
      setShowPDF({ isShow: true, noteName: "Payment Instruction - " + row.manufacturerName, fileName: "Payment Instruction_" + row.manufacturerName });
    };
  }

  const onStripePayment = (invoice: invoiceInfo) => {
    if (!invoice) return;
    setInvoicePopupselectedInvoice(invoice);
    if (invoice?.stripeConfig?.stripe_integration === 'Y') {
      // CS-1458 - Handling zero outstanding amount case for Pay Now
      if (invoice?.remainingAmount && Number(invoice?.remainingAmount) > 0) {
        setShowStripePaymentTypePopup(true);
      } else {
        let errObj: SingleAlertInfo = {
          message: "This invoice currently shows 'Unpaid,' but the outstanding amount due is 0. It was likely settled by credits or adjustments, so no payment is needed.",
          alertType: "error"
        };
        dispatch(setSingleAlertObj(errObj));
      }
    } else if (invoice?.hasPaymentInstruction) {
      if (invoice?.paymentInstructionType === 'Product Document' && invoice?.paymentInstructionValue) {
        if (invoice?.paymentInstructionValue?.startsWith('https://') || invoice?.paymentInstructionValue?.startsWith('http://')) {
          window.open(invoice.paymentInstructionValue, '_blank');
        } else {
          handleOpenPaymentInstructionDoc(invoice);
        }
      } else if (invoice?.paymentInstructionType === 'External URL' && invoice?.paymentInstructionValue) {
        window.open(invoice.paymentInstructionValue, '_blank');
      }
    }
  }

  const handleCheckout = async (invoiceId: number, paymentMethodType: string, paymentType: string, paymentAmount: number) => {
    const data = {
      returnPath: location.pathname + location.search,
      invoiceId: invoiceId,
      paymentMethod: paymentMethodType,
      invoiceLineItemList: [
        {
          invoiceId: invoiceId,
          paymentAmt: paymentAmount,
          paymentType: paymentType,
        }
      ],
    };
    await dispatch(stripeCheckOutPaymentData(data)).then((res) => {
      //CS-1563
      if (res && res?.payload) {
        if (res.payload.errors && res.payload.errors.length > 0) {
          const errorMessage = res.payload.errors[0].errorMessage;
          const alertObj: SingleAlertInfo = {
            message: errorMessage,
            alertType: "error",
          };
          dispatch(setSingleAlertObj(alertObj));
        } else if (res.payload.data) {
          const checkoutUrl = res.payload.data.checkoutUrl;
          if (checkoutUrl) window.location.href = checkoutUrl;
        }
      }
    }).catch((error) => {
      console.log(error);
    });
  };

  //CS-1745 - START
  const onOpenPractice = async (data: practiceFormData) => {
    const response = await dispatch(fetchPracticeById((data.practice).toString()));
    if (response.payload.data && response.payload.message === 'SUCCESS') {
      const { practiceId, associateAgreementDocumentId } = response.payload.data;
      navigate("/practice_edit_view", { state: { practiceId: practiceId, documentId: associateAgreementDocumentId } });
    }
  };

  const onOpenProduct = (data: productFormData) => {
    navigate(`/product?id=${data.product}`);
  };

  // const onOpenManufacturer = async (data: ManufacturerFormData) => {
  //   const response = await dispatch(fetchManufacturerDataById(parseInt(data.manufacturer)));
  //   if (response.payload.data && response.payload.data.message === 'SUCCESS') {
  //     const { manufacturerName, notificationEmail, customConfig, contactPhone, manufacturerId } = response.payload.data;
  //     navigate("/manufacturer_create", {
  //       state: { mfrData: { manufacturerName, notificationEmail, customConfig, contactPhone, manufacturerId } },
  //     });
  //   }
  // };

  const onOpenAgreement = async (data: practiceFormData) => {
    navigate("/create_new_agreement", { state: { practiceId: data.practice } })
  }

  const onOpenUserManagement = async (data: userFormData) => {
    const response = await dispatch(fetchUserProfileById(Number(data.user)));
    if (response.payload.data && response.payload.data.message === 'SUCCESS') {
      const { userName, userId, firstName, lastName, email, phone } = response.payload.data;
      navigate("/user_create", {
        state: { user: { userName, userId, firstName, lastName, email, phone } },
      });
    }
  }

  const onOpenRecordCreditMemo = async (data: RecordCreditMemoFormData) => {
    const response = await dispatch(getInvoiceById({ invoiceId: Number(data.invoice), isSaveAuditLog: true, skipLoader: true }));
    if (response?.payload?.code === SUCCESS_CODE && response?.payload?.data) {
      const { invoiceId, mfgInvoiceNumber, invoiceAmount, mfgInvoiceDate, practiceName, agencyName, mfgName } = response.payload.data;
      navigate("/record_credit_memo", { state: { invoiceId: invoiceId, invNumber: mfgInvoiceNumber, invAmt: invoiceAmount, invDate: mfgInvoiceDate, practice: practiceName, agency: agencyName, mfr: mfgName } });
    }
  }
  //CS-1745 - END
  
  const visibleActionCount = quickActions.filter(a => a.show).length;

  const headerAdornment = isRestrictedUser ? (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.1,
        py: 0.35,
        borderRadius: "999px",
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(148,163,184,0.2)" : "rgba(100,116,139,0.2)"}`,
        backgroundColor: theme.palette.mode === "dark" ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          animation: "pulseIcon 2s ease-in-out infinite",
          "@keyframes pulseIcon": {
            "0%, 100%": { opacity: 1, transform: "scale(1)" },
            "50%": { opacity: 0.5, transform: "scale(0.85)" },
          },
        }}
      >
        <Warning sx={{ fontSize: 18 }} color="warning" />
      </Box>
      <Typography
        sx={{
          fontSize: "0.68rem",
          fontWeight: 500,
          letterSpacing: "0.02em",
          color: theme.palette.mode === "dark" ? "#94a3b8" : "#64748b",
        }}
      >
        The content shown is demonstrative and intended for preview only.
      </Typography>
    </Box>
  ) : null;

  return (
    <Box>
      {/* <Box
        sx={{
          overflow: "hidden",
          mb: 2,
          background:
            theme.palette.mode === "dark"
              ? "linear-gradient(90deg, rgba(20,184,166,0.04) 0%, rgba(20,184,166,0.08) 50%, rgba(20,184,166,0.04) 100%)"
              : "linear-gradient(90deg, rgba(20,184,166,0.03) 0%, rgba(20,184,166,0.06) 50%, rgba(20,184,166,0.03) 100%)",
          py: 0.7,
        }}
      >
        <Typography
          component="div"
          sx={{
            display: "inline-block",
            whiteSpace: "nowrap",
            color: "#14b8a6",
            fontSize: "0.8rem",
            fontWeight: 500,
            letterSpacing: "0.02em",
            animation: "marqueeScroll 25s linear infinite",
            "@keyframes marqueeScroll": {
              "0%": { transform: "translateX(100vw)" },
              "100%": { transform: "translateX(-100%)" },
            },
          }}
        >
          🚧 &nbsp; This page is currently under development. Some features may not be available or function correctly.
          &nbsp;&nbsp;&nbsp; 🚧 &nbsp; This page is currently under development. Some features may not be available or function correctly. &nbsp;&nbsp;&nbsp; 🚧
        </Typography>
      </Box> */}

      <Box m={2}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              p: 0.5,
              borderRadius: 2,
              position: "relative",
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#e5e9ed"}`,
            }}
          >
            {(["dashboard", "analytics"] as const).map((tab) => (
              <Box
                key={tab}
                onClick={() => setActiveTab(tab)}
                sx={{
                  px: 2.5,
                  py: 0.65,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: activeTab === tab ? 600 : 500,
                  letterSpacing: "0.01em",
                  userSelect: "none",
                  position: "relative",
                  zIndex: 1,
                  color: activeTab === tab ? "#fff" : theme.palette.mode === "dark" ? "#94a3b8" : "#64748b",
                  transition: "color 0.25s ease",
                  "&:hover": { color: activeTab === tab ? "#fff" : theme.palette.mode === "dark" ? "#e5e9ed" : "#334155" },
                }}
              >
                {activeTab === tab && (
                  <motion.div
                    layoutId="tab-indicator"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                    style={{ position: "absolute", inset: 0, borderRadius: 6, background: "linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)", boxShadow: "0 2px 8px rgba(20,184,166,0.35)", zIndex: -1 }}
                  />
                )}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </Box>
            ))}
          </Box>
        </Box>

        <AnimatePresence mode="wait">
          {activeTab === "dashboard" && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <Box mb={2.5}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ mb: 2, letterSpacing: "-0.01em", color: theme.palette.mode === "dark" ? "#e5e9ed" : "#1e293b" }}
                >
                  Quick Actions
                </Typography>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} md={9}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: {
                          xs: "repeat(1, 1fr)",
                          sm: `repeat(${Math.min(2, visibleActionCount)}, 1fr)`,
                          md: `repeat(${Math.min(3, visibleActionCount)}, 1fr)`
                        }, gap: 2, height: "100%"
                      }}>
                      {quickActions.filter(action => action.show).map((action) => (
                        <Box
                          key={action.label}
                          onClick={() =>
                            action.onClick
                              ? action.onClick()
                              : action.route
                                ? navigate(action.route)
                                : undefined
                          }
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                            py: 2.5,
                            borderRadius: 3,
                            cursor: "pointer",
                            position: "relative",
                            overflow: "hidden",
                            backgroundColor: theme.palette.mode === "dark" ? "rgba(26,28,35,0.8)" : "#ffffff",
                            backdropFilter: "blur(8px)",
                            border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "#e5e9ed"}`,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0, left: 0, right: 0,
                              height: "1px",
                              background: "linear-gradient(90deg, transparent, rgba(20,184,166,0.0), transparent)",
                              transition: "all 0.3s ease",
                            },
                            "&:hover": {
                              borderColor: "rgba(20,184,166,0.3)",
                              backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.06)" : "rgba(20,184,166,0.04)",
                              transform: "translateY(-3px)",
                              boxShadow: theme.palette.mode === "dark"
                                ? "0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(20,184,166,0.1)"
                                : "0 8px 32px rgba(0,0,0,0.08), 0 0 0 1px rgba(20,184,166,0.1)",
                              "&::before": { background: "linear-gradient(90deg, transparent, rgba(20,184,166,0.4), transparent)" },
                            },
                            "&:active": { transform: "translateY(-1px)" },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {action.icon}
                          </Box>
                          <Typography
                            sx={{ fontSize: "0.84rem", fontWeight: 500, letterSpacing: "0.01em", color: theme.palette.mode === "dark" ? "#cbd5e1" : "#475569" }}
                          >
                            {action.label}
                          </Typography>
                        </Box>
                      ))}

                    </Box>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TimePanel />
                      </Grid>
                      <Grid item xs={12}>
                        {dashboardGlobalStyles}
                        {canAccessBrowseMarket && (visibleActionCount <= 3 ? (
                          <Box
                            onClick={() => navigate("/products/explore_market")}
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              gap: 1.2,
                              px: 2,
                              py: 1.5,
                              borderRadius: 2.5,
                              cursor: "pointer",
                              position: "relative",
                              overflow: "hidden",
                              background: theme.palette.mode === "dark"
                                ? "linear-gradient(135deg, rgba(20,184,166,0.1) 0%, rgba(20,184,166,0.03) 100%)"
                                : "linear-gradient(135deg, rgba(20,184,166,0.07) 0%, rgba(20,184,166,0.02) 100%)",
                              border: "1px solid rgba(20,184,166,0.18)",
                              transition: "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
                              animation: isRestrictedUser ? "border-glow-pulse 2.8s ease-in-out infinite" : "none",
                              "&:hover": {
                                borderColor: "rgba(20,184,166,0.5)",
                                transform: "translateY(-1px)",
                                boxShadow: "0 6px 20px rgba(20,184,166,0.14), 0 0 0 1px rgba(20,184,166,0.12)",
                              },
                            }}
                          >
                            {isRestrictedUser && (
                              <>
                                <Box sx={{ position: "absolute", top: 8, right: 8, width: 10, height: 10, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.6)", animation: "rp1 2.4s cubic-bezier(0,0,0.2,1) infinite", zIndex: 2 }} />
                                <Box sx={{ position: "absolute", top: 8, right: 8, width: 10, height: 10, borderRadius: "50%", border: "1.5px solid rgba(255,255,255,0.4)", animation: "rp2 2.4s cubic-bezier(0,0,0.2,1) infinite 0.55s", zIndex: 2 }} />
                                <Box sx={{ position: "absolute", top: 11, right: 11, width: 4, height: 4, borderRadius: "50%", backgroundColor: "#ffffff", animation: "dot-breathe 1.8s ease-in-out infinite", zIndex: 3 }} />
                              </>
                            )}
                            <Box sx={{ width: 30, height: 30, borderRadius: "8px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.12)" : "rgba(255,255,255,0.2)", border: theme.palette.mode === "dark" ? "1px solid rgba(20,184,166,0.22)" : "1px solid rgba(255,255,255,0.25)" }}>
                              <ShoppingBag size={15} color={theme.palette.mode === "dark" ? "#14b8a6" : "#ffffff"} strokeWidth={1.8} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#ffffff", lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                Explore Market
                              </Typography>
                              <Typography sx={{ fontSize: "0.7rem", py: 0.5, color: "rgba(255,255,255,0.8)", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                Discover new products and expand your offerings
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#ffffff", whiteSpace: "nowrap" }}>Browse Now</Typography>
                                <Box component="span" sx={{ color: "#ffffff", fontSize: "0.75rem" }}>→</Box>
                              </Box>
                            </Box>

                          </Box>
                        ) : (
                        <Box
                          onClick={() => navigate("/products/explore_market")}
                          sx={{
                            flexShrink: 0,
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            gap: 1.5,
                            p: 2,
                            borderRadius: 3,
                            cursor: "pointer",
                            position: "relative",
                            overflow: "hidden",
                            background: theme.palette.mode === "dark"
                              ? "linear-gradient(160deg, rgba(20,184,166,0.11) 0%, rgba(20,184,166,0.04) 100%)"
                              : "linear-gradient(160deg, rgb(13, 148, 136,0.5) 0%, rgb(22, 163, 74,0.8) 100%)",
                            border: "1px solid rgba(20,184,166,0.15)",
                            transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                            animation: isRestrictedUser ? "border-glow-pulse 2.8s ease-in-out infinite" : "none",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              bottom: 0, left: 0, right: 0,
                              height: "40%",
                              background: "linear-gradient(0deg, rgba(20,184,166,0.04) 0%, transparent 100%)",
                              pointerEvents: "none",
                            },
                            "&:hover": {
                              borderColor: "rgba(20,184,166,0.5)",
                              transform: "translateY(-3px)",
                              boxShadow: "0 12px 40px rgba(20,184,166,0.18), 0 0 0 1px rgba(20,184,166,0.15)",
                            },
                          }}
                        >
                          {/* ── Radar rings + bubbles (PRU only) ── */}
                          {isRestrictedUser && (
                            <>
                              {/* Ring 1 */}
                              <Box sx={{
                                position: "absolute", top: 14, right: 14,
                                width: 16, height: 16, borderRadius: "50%",
                                border: "1.5px solid rgba(255,255,255,0.7)",
                                animation: "rp1 2.4s cubic-bezier(0,0,0.2,1) infinite",
                                zIndex: 2,
                              }} />
                              {/* Ring 2 */}
                              <Box sx={{
                                position: "absolute", top: 14, right: 14,
                                width: 16, height: 16, borderRadius: "50%",
                                border: "1.5px solid rgba(255,255,255,0.5)",
                                animation: "rp2 2.4s cubic-bezier(0,0,0.2,1) infinite 0.55s",
                                zIndex: 2,
                              }} />
                              {/* Ring 3 */}
                              <Box sx={{
                                position: "absolute", top: 14, right: 14,
                                width: 16, height: 16, borderRadius: "50%",
                                border: "1.5px solid rgba(255,255,255,0.3)",
                                animation: "rp3 2.4s cubic-bezier(0,0,0.2,1) infinite 1.1s",
                                zIndex: 2,
                              }} />
                              {/* Center dot */}
                              <Box sx={{
                                position: "absolute", top: 19, right: 19,
                                width: 6, height: 6, borderRadius: "50%",
                                backgroundColor: "#ffffff",
                                animation: "dot-breathe 1.8s ease-in-out infinite",
                                zIndex: 3,
                              }} />
                              {/* Bubble 1 */}
                              <Box sx={{
                                position: "absolute", bottom: 22, left: "28%",
                                width: 5, height: 5, borderRadius: "50%",
                                backgroundColor: "rgba(255,255,255,0.5)",
                                animation: "float-up-1 3.2s ease-in-out infinite",
                                opacity: 0,
                              }} />
                              {/* Bubble 2 */}
                              <Box sx={{
                                position: "absolute", bottom: 18, left: "52%",
                                width: 4, height: 4, borderRadius: "50%",
                                backgroundColor: "rgba(255,255,255,0.4)",
                                animation: "float-up-2 3.2s ease-in-out infinite 1.1s",
                                opacity: 0,
                              }} />
                              {/* Bubble 3 */}
                              <Box sx={{
                                position: "absolute", bottom: 24, left: "40%",
                                width: 4, height: 4, borderRadius: "50%",
                                backgroundColor: "rgba(255,255,255,0.45)",
                                animation: "float-up-3 3.2s ease-in-out infinite 2s",
                                opacity: 0,
                              }} />
                            </>
                          )}

                          <Box sx={{
                            width: 44, height: 44, borderRadius: "12px",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.12)" : "rgba(255,255,255,0.2)",
                            border: theme.palette.mode === "dark" ? "1px solid rgba(20,184,166,0.2)" : "1px solid rgba(255,255,255,0.25)",
                            boxShadow: theme.palette.mode === "dark" ? "0 0 12px rgba(20,184,166,0.2)" : "none",
                          }}>
                            <ShoppingBag size={22} color={theme.palette.mode === "dark" ? "#14b8a6" : "#ffffff"} strokeWidth={1.8} />
                          </Box>

                          <Typography sx={{ fontSize: "1rem", fontWeight: 700, letterSpacing: "-0.01em", color: "#ffffff" }}>
                            Explore Market
                          </Typography>
                          <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.8)", lineHeight: 1.55 }}>
                            Discover new products and expand your offerings
                          </Typography>

                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: "#ffffff" }}>Browse Now</Typography>
                            <Box component="span" sx={{ color: "#ffffff", fontSize: "0.84rem" }}>→</Box>
                          </Box>
                        </Box>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>

                {/* ── Communication Section ── */}
                {(canAccessInbox || canAccessNotifications) && userInfo?.notificationEnable && (
                <Box mt={3.5} mb={5}>
                  <Grid container spacing={2.5} sx={{ alignItems: "stretch" }}>
                    {canAccessNotifications && (
                      <Grid item xs={12} md={canAccessInbox ? 6 : 12} sx={{ display: "flex" }}>
                        <Box
                          sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1 }}
                          onClickCapture={
                            isRestrictedUser
                              ? (e) => { e.stopPropagation(); withPRUGuard(() => {})(); }
                              : undefined
                          }
                        >
                          <NotificationContainer headerAdornment={headerAdornment}/>
                        </Box>
                      </Grid>
                    )}
                    {canAccessInbox && (
                      <Grid item xs={12} md={canAccessNotifications ? 6 : 12} sx={{ display: "flex" }}>
                        <Box
                          sx={{ width: "100%", display: "flex", flexDirection: "column", flex: 1 }}
                          onClickCapture={
                            isRestrictedUser
                              ? (e) => { e.stopPropagation(); withPRUGuard(() => {})(); }
                              : undefined
                          }
                        >
                          <InboxContainer  headerAdornment={headerAdornment}/>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
              </Box>
            </motion.div>
          )}

          {/* ── ANALYTICS TAB ── */}
          {activeTab === "analytics" && (
            <motion.div
              key="analytics"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: "easeOut" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                  pb: 2.5,
                  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#e5e9ed"}`,
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    sx={{
                      letterSpacing: "-0.02em",
                      color:
                        theme.palette.mode === "dark" ? "#e5e9ed" : "#1e293b",
                      lineHeight: 1.2,
                    }}
                  >
                    Analytics
                  </Typography>
                  <Typography
                    sx={{ color: "#64748b", fontSize: "0.84rem", mt: 0.4 }}
                  >
                    Live KPIs, breakdowns & 12-month trends
                  </Typography>
                </Box>

                {dashboardData?.lastUpdatedAt && (
                  <Box
                    sx={{
                      px: 2,
                      py: 0.7,
                      borderRadius: 2,
                      fontSize: "0.78rem",
                      fontWeight: 500,
                      color: "#14b8a6",
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? "rgba(20,184,166,0.08)"
                          : "rgba(20,184,166,0.06)",
                      border: "1px solid rgba(20,184,166,0.18)",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.8,
                    }}
                  >
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: "#14b8a6",
                        boxShadow: "0 0 6px #14b8a6",
                        animation: "pulse 2s ease-in-out infinite",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.4 },
                        },
                      }}
                    />
                    Live data
                  </Box>
                )}
              </Box>

              <Box mb={1}>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "#475569",
                    mb: 1.5,
                  }}
                >
                  Key Metrics
                </Typography>

                <Grid container spacing={2}>
                  {canAccessActiveUserKPI && (
                    <Grid item xs={12} sm={6} md={4} xl={3}>
                      <TotalActiveUsersCard
                        title="Active Users"
                        total={userStats?.activeUsers || 0}
                        percentage={userStats?.userComparison || 0}
                        duration="4 weeks"
                        chartData={
                          activeUsersKPIBarData?.counts ?? [0, 0, 0, 0]
                        }
                        color="success"
                        labels={
                          activeUsersKPIBarData?.ranges ?? [
                            "W1",
                            "W2",
                            "W3",
                            "W4",
                          ]
                        }
                        tooltipMessage="A user that has logged in within the last four weeks."
                        loading={!userKPIReady}
                        kpiType="activeUsers"
                        onOpenModal={handleOpenKPIModal}
                      />
                    </Grid>
                  )}
                  {canAccessActivePracticeKPI && (
                    <Grid item xs={12} sm={6} md={4} xl={3}>
                      <TotalActiveUsersCard
                        title="Active Practices"
                        total={practiceStats?.activePractices || 0}
                        percentage={practiceStats?.practiceComparison || 0}
                        duration="4 weeks"
                        chartData={
                          activePracticesKPIBarData?.counts ?? [0, 0, 0, 0]
                        }
                        color="info"
                        labels={
                          activePracticesKPIBarData?.ranges ?? [
                            "W1",
                            "W2",
                            "W3",
                            "W4",
                          ]
                        }
                        tooltipMessage="At least one IVR or Order associated with that practice has been submitted within the last 4 weeks."
                        loading={!practiceKPIReady}
                        kpiType="activePractices"
                        onOpenModal={handleOpenKPIModal}
                      />
                    </Grid>
                  )}
                  {canAccessPatientsKPI && (
                    <Grid item xs={12} sm={6} md={4} xl={3}>
                      <TotalActiveUsersCard
                        title="Total Patients"
                        total={patientStats?.totalPatients || 0}
                        percentage={patientStats?.patientComparison || 0}
                        duration="4 weeks"
                        chartData={
                          totalPatientsKPIBarData?.counts ?? [0, 0, 0, 0]
                        }
                        color="cyan"
                        labels={
                          totalPatientsKPIBarData?.ranges ?? [
                            "W1",
                            "W2",
                            "W3",
                            "W4",
                          ]
                        }
                        tooltipMessage="Shows total number of patients"
                        loading={!patientKPIReady}
                        kpiType="totalPatients"
                        onOpenModal={handleOpenKPIModal}
                      />
                    </Grid>
                  )}
                  {canAccessWeeklySalesKPI && (
                    <Grid item xs={12} sm={6} md={4} xl={3}>
                      <TotalActiveUsersCard
                        title="Weekly Sales"
                        total={salesStats?.weeklySales || 0}
                        percentage={salesStats?.salesComparison || 0}
                        duration="7 days"
                        chartData={
                          weeklySalesKPIBarData?.counts ?? [0, 0, 0, 0]
                        }
                        color="warning"
                        labels={
                          weeklySalesKPIBarData?.ranges ?? [
                            "W1",
                            "W2",
                            "W3",
                            "W4",
                          ]
                        }
                        tooltipMessage="Shows the COGs total for the previous 7 calendar days of orders."
                        loading={!salesKPIReady}
                        kpiType="weeklySales"
                        onOpenModal={handleOpenKPIModal}
                      />
                    </Grid>
                  )}
                  {canAccessWeeklyOrdersKPI && (
                    <Grid item xs={12} sm={6} md={4} xl={3}>
                      <TotalActiveUsersCard
                        title="Weekly Orders"
                        total={weeklyOrdersStats?.weeklyOrders || 0}
                        percentage={weeklyOrdersStats?.orderComparison || 0}
                        duration="7 days"
                        chartData={
                          weeklyOrdersKPIBarData?.counts ?? [0, 0, 0, 0]
                        }
                        color="purple"
                        labels={
                          weeklyOrdersKPIBarData?.ranges ?? [
                            "W1",
                            "W2",
                            "W3",
                            "W4",
                          ]
                        }
                        tooltipMessage="Shows total number of orders for the previous 7 calendar days."
                        loading={!ordersKPIReady}
                        kpiType="weeklyOrders"
                        onOpenModal={handleOpenKPIModal}
                      />
                    </Grid>
                  )}
                  {canAccessRecordTxKPI && (
                    <Grid item xs={12} sm={6} md={4} xl={3}>
                      <TotalActiveUsersCard
                        title="Record Tx"
                        total={
                          weeklyRecordTxPercentageStats?.weeklyRecordTx || 0
                        }
                        percentage={
                          weeklyRecordTxPercentageStats?.weeklyRecordTxComparison ||
                          0
                        }
                        duration="7 days"
                        chartData={
                          weeklyRecordTxKPIBarData?.counts ?? [0, 0, 0, 0]
                        }
                        color="success"
                        labels={
                          weeklyRecordTxKPIBarData?.ranges ?? [
                            "W1",
                            "W2",
                            "W3",
                            "W4",
                          ]
                        }
                        tooltipMessage="Shows the percentage of items ordered over the previous 7 calendar days that have been recorded as applied to a patient."
                        loading={!weeklyRecordTxKPIReady}
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box
                sx={{
                  my: 3,
                  height: "1px",
                  background:
                    theme.palette.mode === "dark"
                      ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent)"
                      : "linear-gradient(90deg, transparent, rgba(0,0,0,0.08), transparent)",
                }}
              />

              {canAccessIVRDonut || canAccessOrderDonut ? (
                <Box>
                  <Typography
                    sx={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      color: "#475569",
                      mb: 1.5,
                    }}
                  >
                    Breakdown & Trends
                  </Typography>

                  <Grid container spacing={2.5} alignItems="stretch">
                    <Grid item xs={12} md={5} lg={4} sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          overflow: "hidden",
                          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#e5e9ed"}`,
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#191c24"
                              : "#ffffff",
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            px: 2.5,
                            py: 2,
                            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#e5e9ed"}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.95rem",
                              color:
                                theme.palette.mode === "dark"
                                  ? "#e5e9ed"
                                  : "#1e293b",
                            }}
                          >
                            Status Breakdown
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.6,
                            }}
                          >
                            {canAccessIVRDonut && (
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.35,
                                  borderRadius: 10,
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  backgroundColor: "rgba(20,184,166,0.12)",
                                  color: "#14b8a6",
                                  border: "1px solid rgba(20,184,166,0.25)",
                                }}
                              >
                                IVR
                              </Box>
                            )}
                            {canAccessOrderDonut && (
                              <Box
                                sx={{
                                  px: 1.5,
                                  py: 0.35,
                                  borderRadius: 10,
                                  fontSize: "0.72rem",
                                  fontWeight: 600,
                                  backgroundColor: "rgba(250,204,21,0.1)",
                                  color: "#ca8a04",
                                  border: "1px solid rgba(250,204,21,0.25)",
                                }}
                              >
                                Orders
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ p: 1, flex: 1 }}>
                          <StatusBreakdown
                            key={`analytics-${dashboardData?.lastUpdatedAt}`}
                            loading={statusLoading}
                          />
                        </Box>
                      </Box>
                    </Grid>

                    {/* Area Chart — right column */}
                    <Grid item xs={12} md={7} lg={8} sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          borderRadius: 3,
                          overflow: "hidden",
                          border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.06)" : "#e5e9ed"}`,
                          backgroundColor:
                            theme.palette.mode === "dark"
                              ? "#191c24"
                              : "#ffffff",
                          width: "100%",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            px: 2.5,
                            py: 2,
                            borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.05)" : "#e5e9ed"}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.95rem",
                                color:
                                  theme.palette.mode === "dark"
                                    ? "#e5e9ed"
                                    : "#1e293b",
                              }}
                            >
                              12-Month Trend
                            </Typography>
                            <Typography
                              sx={{
                                color: "#64748b",
                                fontSize: "0.78rem",
                                mt: 0.2,
                              }}
                            >
                              IVR & Order volume over the last 12-months
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            {canAccessIVRDonut && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.6,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: "#1bb0a8",
                                  }}
                                />
                                <Typography
                                  sx={{ fontSize: "0.75rem", color: "#64748b" }}
                                >
                                  IVR
                                </Typography>
                              </Box>
                            )}
                            {canAccessOrderDonut && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.6,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 3,
                                    borderRadius: 2,
                                    backgroundColor: "#facc15",
                                  }}
                                />
                                <Typography
                                  sx={{ fontSize: "0.75rem", color: "#64748b" }}
                                >
                                  Orders
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ px: 1, pt: 1, pb: 0, flex: 1 }}>
                          <AreaChart
                            options={areaChartOptions}
                            series={areaChartSeries as any}
                            height={350}
                            loading={areaLoading}
                          />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              ) : (
                <Paper
                  elevation={0}
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1A1C23" : "#ffffff",
                    p: 4,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.mode === "dark" ? "#2A2D38" : "#e5e9ed"}`,
                    textAlign: "center",
                  }}
                >
                  <Typography sx={{ color: "#94a3b8", fontSize: "0.95rem" }}>
                    No analytics data available for your account.
                  </Typography>
                </Paper>
              )}
              <Box mb={4} />
            </motion.div>
          )}
        </AnimatePresence>

        <KPIDetailModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          kpiType={selectedKPIType}
          totalValue={
            selectedKPIType === "weeklySales"
              ? salesStats?.weeklySales
              : selectedKPIType === "weeklyOrders"
                ? weeklyOrdersStats?.weeklyOrders
                : undefined
          }
        />
      </Box>

      <OrderProductModal
        open={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
      />
      {/* CS-1758 - Shared Add New Patient popup for dashboard quick action */}
      <AddNewPatientPopup
        open={showAddNewPatientPopup}
        onClose={() => setShowAddNewPatientPopup(false)}
        onManualEntry={handleManualEntry}
        showFacesheetOption={canCreatePatientUsingFaceSheet}
      />
      {addPatientOpen &&(
        <AddPatientModal
          open={addPatientOpen}
          onClose={() => setAddPatientOpen(false)}
        />
      )}
      <CreateIVRModal
        open={createIVROpen}
        onClose={() => setCreateIVROpen(false)}
      />
      <RecordTreatmentModal
        open={recordTreatmentOpen}
        onClose={() => setRecordTreatmentOpen(false)}
      />
      <ReturnProductModal
        open={returnProductOpen}
        onClose={() => setReturnProductOpen(false)}
      />
      <PayInvoiceModal
        open={payInvoiceOpen}
        onClose={() => setPayInvoiceOpen(false)}
        onRecordPayment={onRecordPayment}
        onStripePayment={onStripePayment}
      />
      {showPRUWarning.show && (
        <WarningPopupNew
          open={showPRUWarning.show}
          onClose={() => setShowPRUWarning({ show: false, message: '' })}
          title={showPRUWarning.title}
          message={<>{showPRUWarning.message}</>}
          variant={showPRUWarning.variant}
          buttons={showPRUWarning.buttons}
        />
      )}
      {showStripePaymentTypePopup && (
        <StripePaymentTypePopup
          open={showStripePaymentTypePopup}
          onClose={() => setShowStripePaymentTypePopup(false)}
          invoiceData={invoicePopupselectedInvoice}
          createCheckoutSession={(handleCheckout)}
        />
      )}
      {showPDF && (
        <PDFViewer
          isOpen={showPDF.isShow}
          onClose={() =>
            setShowPDF({ isShow: false, noteName: "", fileName: "" })
          }
          title={showPDF.noteName}
          fileName={showPDF.fileName}
          base64String={pdfContent || ""}
        />
      )}
      <CompletePracticeRegistrationModal
        open={registrationModalOpen}
        onClose={() => setRegistrationModalOpen(false)}
      />
      <ManagePracticeModal
        open={managePracticeModalOpen}
        onClose={() => setManagePracticeModalOpen(false)}
        onSubmit={onOpenPractice}
      />
      <ManageProductModal
        open={manageProductModalOpen}
        onClose={() => setManageProductModalOpen(false)}
        onSubmit={onOpenProduct}
      />
      {/* <ManageManufacturerModal
        open={manageManufacturerModalOpen}
        onClose={() => setManageManufacturerModalOpen(false)}
        onSubmit={onOpenManufacturer}
      /> */}
      <CreateOrApplyInvoiceModal
        open={createOrApplyInvModalOpen}
        onClose={() => setCreateOrApplyInvModalOpen(false)}
      />
      <ManagePracticeModal
        open={createAgreementModalOpen}
        onClose={() => setCreateAgreementModalOpen(false)}
        onSubmit={onOpenAgreement}
        showAddPractice={false}
      />
      <ManageUserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        onSubmit={onOpenUserManagement}
      />
      <RecordCreditMemoModal
        open={recordCreditMemoOpen}
        onClose={() => setRecordCreditMemoOpen(false)}
        onSubmit={onOpenRecordCreditMemo}
      />
    </Box>
  );
};

export default Dashboard;
